import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ExportButton, type ExportFormat } from '@/components/atoms/ExportButton/ExportButton';
import { trackAnalyticsExport } from '@/modules/analytics/utils/analyticsTracking';
import { buildExportFilename } from '@/modules/analytics/utils/exportFilename';
import { PageName } from '@/shared/types/segment';
import { downloadCsv, downloadJson } from '@/util/fileManager';

import { useAiAgentsInsights, type AiAgentsInsightsData } from './hooks/useAiAgentsInsights';

interface MergedTimeseriesRow {
  time_bucket: string;
  /** Pooled per-day CSAT, same quantity and column name as the Overview export. */
  avg_csat_score: number | null;
  containment_rate: number | null;
  avg_response_time_seconds: number | null;
  total_conversations: number | null;
}

const mergeTimeseriesByDate = (insights: AiAgentsInsightsData): MergedTimeseriesRow[] => {
  const byDate = new Map<string, MergedTimeseriesRow>();

  const ensure = (date: string): MergedTimeseriesRow => {
    const existing = byDate.get(date);
    if (existing) {
      return existing;
    }
    const row: MergedTimeseriesRow = {
      time_bucket: date,
      avg_csat_score: null,
      containment_rate: null,
      avg_response_time_seconds: null,
      total_conversations: null,
    };
    byDate.set(date, row);
    return row;
  };

  for (const point of insights.timeseries.csat) {
    ensure(point.date).avg_csat_score = point.value;
  }
  for (const point of insights.timeseries.containment) {
    ensure(point.date).containment_rate = point.value;
  }
  for (const point of insights.timeseries.responseTime) {
    ensure(point.date).avg_response_time_seconds = point.value;
  }
  for (const point of insights.timeseries.totalConversations) {
    ensure(point.date).total_conversations = point.value;
  }

  return Array.from(byDate.values()).sort((a, b) => a.time_bucket.localeCompare(b.time_bucket));
};

const buildPayload = (insights: AiAgentsInsightsData) => ({
  kpi: {
    avg_ai_csat: insights.kpiValues.csat.current,
    containment_rate: insights.kpiValues.containment.current,
    avg_response_time_seconds: insights.kpiValues.responseTime.current,
    total_conversations: insights.kpiValues.totalConversations.current,
    avg_csat_user_rating: insights.csatBreakdown.avgUserRating,
    csat_user_rating_count: insights.csatBreakdown.userRatingCount,
    avg_ai_csat_insights: insights.csatBreakdown.avgAiInsights,
    ai_csat_insights_count: insights.csatBreakdown.aiInsightsCount,
  },
  timeseries: mergeTimeseriesByDate(insights),
});

/**
 * Per-widget icon export button for the AI Agents Insights section.
 *
 * Mirrors the icon-variant `ExportButton` used in each Overview card: dumps the
 * KPI tile values and the per-bucket timeseries data shown in the chart below.
 */
export const AiAgentsInsightsExport = () => {
  const { t } = useTranslation();
  const { data: insights } = useAiAgentsInsights();
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = useCallback(
    (format: ExportFormat) => {
      try {
        setIsExporting(true);
        trackAnalyticsExport(PageName.ANALYTICS_AI_AGENTS, 'ai_agents_insights', format);

        const payload = buildPayload(insights);
        const filename = buildExportFilename('ai_agents_insights');

        if (format === 'json') {
          downloadJson(payload, filename);
          return;
        }

        const rows: Record<string, unknown>[] = [
          { row_type: 'kpi', ...payload.kpi },
          ...payload.timeseries.map((row) => ({ row_type: 'timeseries', ...row })),
        ];
        downloadCsv(rows, filename);
      } catch {
        toast.error(t('analytics.export_error'));
      } finally {
        setIsExporting(false);
      }
    },
    [insights, t]
  );

  return <ExportButton variant="icon" onExport={handleExport} isExporting={isExporting} />;
};
